import React, { useState, useEffect } from "react";
import { apiRequest } from "../../utils/api";
import FormTemplateForm from "../../components/admin/FormTemplateForm";
import ConfirmModal from "../../components/common/ConfirmModal";
import { useToastContext } from "../../contexts/ToastContext";

function FormTemplates() {
  const toast = useToastContext();
  const [formTemplates, setFormTemplates] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [filters, setFilters] = useState({
    visa_type_id: "",
    status: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  useEffect(() => {
    loadVisaTypes();
  }, []);

  useEffect(() => {
    loadFormTemplates();
  }, [filters, pagination.current_page]);

  const loadVisaTypes = async () => {
    try {
      const res = await apiRequest("/api/admin/visa-types/active/list");
      if (res.ok) {
        const data = await res.json();
        setVisaTypes(data.data);
      }
    } catch (error) {
      console.error("Ошибка загрузки типов виз:", error);
    }
  };

  const loadFormTemplates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== "")
        ),
      });

      const res = await apiRequest(`/api/admin/form-templates?${params}`);
      if (res.ok) {
        const data = await res.json();
        setFormTemplates(data.data);
        setPagination(data.meta);
      }
    } catch (error) {
      console.error("Ошибка загрузки шаблонов:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const handleDelete = async () => {
    const id = deleteConfirm.id;
    if (!id) return;

    try {
      const res = await apiRequest(`/api/admin/form-templates/${id}`, {
        method: "DELETE",
      });

      if (res.ok) {
        toast.success("Шаблон успешно удалён");
        loadFormTemplates();
      } else {
        const data = await res.json();
        throw new Error(data?.error?.message || "Ошибка при удалении");
      }
    } catch (error) {
      console.error("Ошибка удаления:", error);
      toast.error(error.message || "Не удалось удалить шаблон");
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingTemplate(null);
    loadFormTemplates();
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "bg-gray-100 text-gray-700",
      active: "bg-green-100 text-green-700",
      archived: "bg-blue-100 text-blue-700",
    };
    return badges[status] || badges.draft;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Черновик",
      active: "Активен",
      archived: "Архив",
    };
    return labels[status] || status;
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-blue-700">Шаблоны форм</h1>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors"
          >
            + Создать шаблон
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            placeholder="Поиск..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
            className="py-2 px-3 border border-blue-200 rounded-md bg-blue-50 text-blue-700 focus:ring-2 focus:ring-blue-200 outline-none"
          />
          <select
            value={filters.visa_type_id}
            onChange={(e) =>
              setFilters({ ...filters, visa_type_id: e.target.value })
            }
            className="py-2 px-3 border border-blue-200 rounded-md bg-blue-50 text-blue-700 focus:ring-2 focus:ring-blue-200 outline-none"
          >
            <option value="">Все типы виз</option>
            {visaTypes.map((vt) => (
              <option key={vt.id} value={vt.id}>
                {vt.name}
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value })
            }
            className="py-2 px-3 border border-blue-200 rounded-md bg-blue-50 text-blue-700 focus:ring-2 focus:ring-blue-200 outline-none"
          >
            <option value="">Все статусы</option>
            <option value="draft">Черновик</option>
            <option value="active">Активен</option>
            <option value="archived">Архив</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-blue-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-blue-400">Загрузка...</div>
        ) : formTemplates.length === 0 ? (
          <div className="p-8 text-center text-blue-400">
            Шаблоны не найдены
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                    Название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                    Тип визы
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                    Создан
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {formTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-blue-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-700">
                      {template.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-700">
                      {template.visa_type?.name || "—"}
                      {template.visa_type?.country && (
                        <span className="text-blue-400 ml-1">
                          ({template.visa_type.country})
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded ${getStatusBadge(
                          template.status
                        )}`}
                      >
                        {getStatusLabel(template.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-400">
                      {template.created_at
                        ? new Date(template.created_at).toLocaleDateString(
                            "ru-RU"
                          )
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-blue-500 hover:text-blue-700 mr-4"
                      >
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDeleteClick(template.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="px-6 py-4 border-t border-blue-100 flex items-center justify-between">
                <div className="text-sm text-blue-400">
                  Показано {formTemplates.length} из {pagination.total}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        current_page: pagination.current_page - 1,
                      })
                    }
                    disabled={pagination.current_page === 1}
                    className="px-3 py-1 border border-blue-200 rounded text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50"
                  >
                    Назад
                  </button>
                  <span className="px-3 py-1 text-blue-700">
                    {pagination.current_page} / {pagination.last_page}
                  </span>
                  <button
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        current_page: pagination.current_page + 1,
                      })
                    }
                    disabled={pagination.current_page === pagination.last_page}
                    className="px-3 py-1 border border-blue-200 rounded text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50"
                  >
                    Вперёд
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <FormTemplateForm
          formTemplate={editingTemplate}
          onClose={() => {
            setShowForm(false);
            setEditingTemplate(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onConfirm={handleDelete}
        title="Подтверждение удаления"
        message="Вы уверены, что хотите удалить этот шаблон? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        type="danger"
      />
    </div>
  );
}

export default FormTemplates;


